import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import { ActivatedRoute } from '@angular/router';
import { Unsubscribe } from 'redux';
import { CookieService } from 'ngx-cookie-service';

import { TrackingJsonService, UserService } from '../../../api/services';
import { IAppState, IAppConfig, Actions } from '../../../store';
import { redirectToURL } from '../../../common';
import { SiteURLUtils, SiteURL } from '../../../common/site.url.utils';
import { Title } from '@angular/platform-browser';
import { Reservation, Hotel, ReservationErrorMessageKeys, MessageCategory } from '../../../api/model';
import { ActionHandler } from '../../../store/action.handler';
import { AppAction } from '../../../store/interfaces/IAppState';
import { MessagesUtil } from '../../../common/messages.util';
import { IResDirectNumber, IMessage } from '../../../api/interfaces';
import { CancelReservationDelegate } from '../../../api/delegates';
import { APP_CONFIG } from '../../../api/config';
import { RouteNames } from '../../../api/enums';


const USE_CACHE = true;
const PREVIOUS_PAGE = 'PreviousPage';

@Component({
  selector: 'stay-mgmt-cancel-res',
  templateUrl: './cancel-res.component.html',
  styleUrls: ['./cancel-res.component.scss']
})
export class CancelResComponent implements OnInit, OnDestroy {
  error: any = null;
  message: IMessage;
  resDirect: IResDirectNumber;
  reservation: Reservation = null;
  hotel: Hotel = null;
  reservationId: string;
  lastName: string;
  acceptTerms: boolean;
  state: IAppState;
  prevPage: string;

  messages: { [key: string]: string };
  messageCategory: MessageCategory;

  unsubscribe: Unsubscribe;

  constructor(
    @Inject(APP_CONFIG) protected appConfig: IAppConfig,
    private _ngRedux: NgRedux<IAppState>,
    private _cancelRes: CancelReservationDelegate,
    private _activatedRoute: ActivatedRoute,
    private _titleService: Title,
    private _trackingJson: TrackingJsonService,
    private _userService: UserService,
    private _actions: Actions,
    private _cookieService: CookieService
  ) {

    this._activatedRoute.params.subscribe(params => {
      this.reservationId = params['id'];
      this.lastName = params['lastName'];
    });
    this._activatedRoute.queryParams.subscribe(queryParams => {
      this.prevPage = queryParams['prevPage'];
    });
    this._titleService.setTitle('IHG | Cancel Your Reservation');
  }

  ngOnInit() {
    this.message = null;
    //  FIND OUT WHO WE ARE BEFORE DOING ANYTHING ELSE
    this._userService.me();
    //  START TRACKING JSON
    this._trackingJson.subscribeRoute(RouteNames.CancelReservation);
    //  THIS SETS TOP LEVEL COMPONENT VARIABLES BECAUSE USING select() WAS CAUSING PROBLEMS
    this.updateFromState();
    //  THIS IS THE REDUX SUBSCRIPTION THAT GETS CALLED EVERYTIME STATE IS CHANGED
    //  IT THEN REHYDRATES WHATEVER TOP LEVEL VARIABLES HAVE BEEN CHANGED
    //  AND PUSHES THOSE CHANGES INTO THE CHILD COMPONENTS
    this.unsubscribe = this._ngRedux.subscribe(() => {
      this.updateFromState();
    });
    // Check to see if we already have something from the state
    if (this.reservation === null) {
      //  THIS MAKES ALL THE CALLS TO APIS TO POPULATE STATE
      this._cancelRes.fetchReservation(this.reservationId, this.lastName, RouteNames.CancelReservation);
    }
    this.messageCategory = MessageCategory.CANCEL_RESERVATION;
  }

  updateFromState() {
    this.state = this._ngRedux.getState();
    if (!this.state) {
      return;
    }
    this.messages = MessagesUtil.filterTranslations(this.state.translations, 'cancelreservation');
    this.resDirect = this.state.resDirect;
    this.acceptTerms = this.state.acceptTerms;
    this.reservation = this.state.reservation;
    this.hotel = this.state.hotel;
    ActionHandler.handleError(this.state, AppAction.GET_RESERVATION, () => {
      this.error = this.state.error;
      this.message = MessagesUtil.fromError(this.appConfig, this.error, ReservationErrorMessageKeys.RETRIEVE_ERROR_UNKNOWN_KEY);
    });
    ActionHandler.handleError(this.state, AppAction.CANCEL_RESERVATION, () => {
      this.error = this.state.error;
      this.message = MessagesUtil.fromError(this.appConfig, this.error, ReservationErrorMessageKeys.ERROR_UNKNOWN_KEY);
    });
    ActionHandler.handleSuccess(this.state, AppAction.CANCEL_RESERVATION, () => {
      this.message = null;
    });
  }

  ngOnDestroy() {
    this._trackingJson.unsubscribeRoute(RouteNames.CancelReservation);
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }
  onAcceptTermsChange(message: string) {
    this._actions.dispatchAcceptTerms(AppAction.ACCEPT_TERMS, (message === 'true'));
  }

  onDoNotCancel(): void {
    const destinationURL = this.getPageToReturnTo();
    redirectToURL(destinationURL);
  }


  getPageToReturnTo(): string {

    // (1) If the user sources from payment, return to the reservation view
    // (2) If the user sources from Stay Details via account management, the user should be taken to Stay Events.
    // (3) If the user sources from email, the user should be taken to the hotel branded manage View Reservation
    // (4) If the user sources from an unknown url, go to View Reservation


    const confirmationNumber = this.reservation.confirmationNumber;
    const lastName = encodeURIComponent(this.reservation.guest.lastName);
    const prevPage = this.prevPage || '';

    const PAY = 'pay';
    const ACCOUNT_MANAGEMENT = 'accountMgmt';
    let destinationURL = '';

    if (prevPage && confirmationNumber && lastName) {
      // (1) go back to reservation view if we come from payment
      if (prevPage === PAY) {
        destinationURL = SiteURLUtils.getLocalizedReservationViewUrl(this.appConfig, confirmationNumber, lastName);
      } else if (prevPage === ACCOUNT_MANAGEMENT) {

        // (2) go to Stay Events if we came from account management
        destinationURL = SiteURLUtils.getLocalizedStaysEventsUrl(this.appConfig);
      }
    }

    // (3)(4) If from email or from an unknown URL, go to View Reservation
    if (destinationURL.length === 0 && confirmationNumber && lastName) {
      destinationURL = SiteURLUtils.getLocalizedReservationViewUrl(this.appConfig, confirmationNumber, lastName);
    }

    // If for any reason something fails
    if (destinationURL.length === 0) {
      destinationURL = SiteURLUtils.getLocalizedSiteUrl(this.appConfig, SiteURL.HOMEPAGE_6C);
    }

    return destinationURL;
  }

  onCancelReservation(): void {
    this.message = null;
    if (this.acceptTerms && (this.reservation.cancellationNumber === null || this.reservation.cancellationNumber === '')) {
      this._cancelRes.deleteReservation(this.reservation.confirmationNumber, this.reservation.guest.lastName, this.reservation.communicationPreferences);
    }
  }

}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/routes/cancel-res/cancel-res/cancel-res.component.ts